/**
 * Shared BOM-building helpers used by the Shell tab, Nozzle tab, and the
 * Estimation tab.  Every function here is a pure transform — no side-effects,
 * no form-context dependencies.
 */

// ── tiny math helpers ─────────────────────────────────────────────────────────

export function roundToTwo(num, decimalPlaces = 2) {
  num = Math.round(num + "e" + decimalPlaces);
  return Number(num + "e" + -decimalPlaces);
}

export function safeNumber(val) {
  const num = Number(val);
  return Number.isFinite(num) ? num : 0;
}

export function toNearestPowerOfTwoFraction(x) {
  if (!Number.isFinite(x)) return String(x);

  const sign = x < 0 ? "-" : "";
  const a = Math.abs(x);
  const whole = Math.floor(a);
  const frac = a - whole;

  let n16 = Math.round(frac * 16 + Number.EPSILON);
  let W = whole;
  if (n16 === 16) {
    W += 1;
    n16 = 0;
  }

  if (n16 === 0) {
    if (W === 0) return "0";
    return sign + String(W);
  }

  let num = n16;
  let den = 16;
  while (den > 2 && num % 2 === 0) {
    num /= 2;
    den /= 2;
  }

  if (W === 0) return `${sign}${num}/${den}`;
  return `${sign}${W} ${num}/${den}`;
}

// ── nozzle grouping ───────────────────────────────────────────────────────────

export function groupNozzleItems(items) {
  const map = new Map();
  items.forEach((item) => {
    const key = `${item.Component}||${item.Description}`;
    const existing = map.get(key);
    if (existing) {
      existing.Qty += item.Qty || 1;
      existing["Measure/Wt"] =
        (existing["Measure/Wt"] || 0) + (item["Measure/Wt"] || 0);
      existing["FAB Hours"] =
        (Number(existing["FAB Hours"]) || 0) +
        (Number(item["FAB Hours"]) || 0);
    } else {
      map.set(key, { ...item });
    }
  });
  return Array.from(map.values());
}

// ── DGV8 → BOM format ────────────────────────────────────────────────────────

export function buildBomFromDgv8(rows) {
  if (!Array.isArray(rows) || rows.length === 0) return [];
  return rows.map((row, index) => {
    const qtyStr = String(row.Qty ?? "").trim();
    const isDimensionQty = /\bx\b/i.test(qtyStr);
    let qty = row.Qty;
    let dimension = "-";
    let unit = "Nos";
    if (isDimensionQty) {
      const parts = qtyStr.split(/\s*x\s*/i).filter(Boolean);
      if (parts.length >= 2) {
        dimension = `${parts[0]} x ${parts[1]}`;
      } else {
        dimension = qtyStr;
      }
      qty = 1;
      unit = "m";
    }
    const weightKg = parseFloat(row["Measure/Wt"]) || 0;
    const weightLb = weightKg * 2.20462;
    return {
      itemNo: index + 1,
      materialDescription: row.Description || "",
      qty: qty,
      dimension: dimension,
      unit: unit,
      weightLb: Math.round(weightLb * 100) / 100,
    };
  });
}

// ── Shell / Head → DGV8 rows ──────────────────────────────────────────────────

export function buildShellBomDgv8(shellRows, shellEstRows) {
  if (!Array.isArray(shellRows)) return [];

  return shellRows
    .filter((row) => row && row.Component)
    .map((row, idx) => {
      const thk = Number(row["Provided Thk"] || 0);
      const diaMm = Number(row.Diameter || 0);
      const lenMm = Number(row["Can Length"] || 0);
      const material = row.Material || "";
      const isShell =
        typeof row.Component === "string" &&
        row.Component.toLowerCase().includes("shell");
      const isHead =
        typeof row.Component === "string" &&
        row.Component.toLowerCase().includes("head");

      let reqQty = 1;
      if (isShell || isHead) {
        const diaM = diaMm / 1000;
        const lenM = (lenMm || diaMm) / 1000;

        let widthM, lengthM;
        if (isShell) {
          widthM = Math.PI * diaM;
          lengthM = lenM;
        } else {
          const blankDia = 1.084 * diaM + 2 * lenM;
          widthM = blankDia;
          lengthM = blankDia;
        }

        const widthStr = roundToTwo(widthM, 3);
        const lengthStr = roundToTwo(lengthM, 3);

        reqQty = `${widthStr} x ${lengthStr} x ${thk}`;
      }

      const est = (shellEstRows || [])[idx] || {};
      const fabHours =
        (parseFloat(est.Circ_Weld_Hours) || 0) +
        (parseFloat(est.Long_Weld_Hours) || 0) +
        (parseFloat(est.Rolling_Hours) || 0) +
        (parseFloat(est.Dishing_Hours) || 0);

      const description =
        thk || material
          ? `PLATE, ${toNearestPowerOfTwoFraction(thk)}" THK, ${material}`
          : "Custom Item";

      return {
        UID: row.UID || "",
        Code: "",
        Component: row.Component,
        Description: description,
        Qty: reqQty,
        "Procured Qty": "",
        "Measure/Wt": row.Weight || 0,
        "Material Cost/ Unit": 0,
        "Material Cost": 0,
        "FAB Hours": roundToTwo(fabHours),
        "FAB Rate/Hr": 0,
        Remarks: "",
      };
    });
}

// ── Nozzle → DGV8 rows ────────────────────────────────────────────────────────

export function buildNozzleBomDgv8(
  nozzleRows,
  nozzleEstRows,
  nozzleMaterial,
  flangeMaterial,
  padMaterial,
) {
  if (!Array.isArray(nozzleRows)) return [];

  const pipeItems = [];
  const flangeItems = [];
  const padItems = [];

  nozzleRows.forEach((row, index) => {
    if (!row) return;
    const size = row.Size;
    const sch = row["Sch/Thk"] || "";
    const type = row.Type || "";
    const flangeRating = row["Flange Rating"] || "";
    const padOD = Number(row["Pad OD"] || 0);
    const padThk = Number(row["Pad Thk"] || 0);
    const estRow = (nozzleEstRows || [])[index] || {};
    const pipeWeight = safeNumber(estRow["Pipe Weight"]);
    const flangeWeight = safeNumber(estRow["Flange Weight"]);
    const padWeight = safeNumber(estRow["Pad Weight"]);
    const nozzleWeldHours = safeNumber(estRow["Nozzle Weld Hours"]);
    const pipeWeldHours = safeNumber(estRow["Pipe Weld Hours"]);
    const padWeldHours = safeNumber(estRow["Pad Weld Hours"]);
    const boltupHours = safeNumber(estRow["Bolt-up Hours"]);
    const pipeFabHours = nozzleWeldHours + pipeWeldHours + padWeldHours;
    const nozzleUID = row.UID || "";

    if (size) {
      const numericSize = Number(size);
      const pipeDesc =
        numericSize && numericSize < 18
          ? `PIPE, ${numericSize}" SCH. ${sch}, ${nozzleMaterial || ""}`
          : `PLATE ${"1/2"}" THK, ${nozzleMaterial || ""}`;

      pipeItems.push({
        UID: nozzleUID,
        Code: "",
        Component: "Pipe",
        Description: pipeDesc,
        Qty: 1,
        "Procured Qty": "",
        "Measure/Wt": pipeWeight,
        "Material Cost/ Unit": 0,
        "Material Cost": 0,
        "FAB Hours": pipeFabHours,
        "FAB Rate/Hr": 0,
        Remarks: "",
      });
    }

    if (flangeRating !== "-" && type !== "-") {
      const flangeDesc = `FLANGE, ${Number(
        size,
      )}", Class ${flangeRating} - Sch. ${sch}, ${type}, ${
        flangeMaterial || ""
      }`;
      flangeItems.push({
        UID: nozzleUID,
        Code: "",
        Component: "Flange",
        Description: flangeDesc,
        Qty: 1,
        "Procured Qty": "",
        "Measure/Wt": flangeWeight,
        "Material Cost/ Unit": 0,
        "Material Cost": 0,
        "FAB Hours": boltupHours,
        "FAB Rate/Hr": 0,
        Remarks: "",
      });
    }

    if (!(padOD === 0 && padThk === 0)) {
      const padDesc = `PAD, OD ${padOD} mm, THK ${padThk} mm, ${
        padMaterial || ""
      }`;

      padItems.push({
        UID: nozzleUID,
        Code: "",
        Component: "Pad",
        Description: padDesc,
        Qty: 1,
        "Procured Qty": "",
        "Measure/Wt": padWeight,
        "Material Cost/ Unit": 0,
        "Material Cost": 0,
        "FAB Hours": 0,
        "FAB Rate/Hr": 0,
        Remarks: "",
      });
    }
  });

  return groupNozzleItems([...pipeItems, ...flangeItems, ...padItems]);
}

// ── Painting area from the Shell tab (DataGridView5) ───────────────────────────
// External surface area in sq.m:
//   shell rows: pi * D_out * L
//   head rows (2:1 SE): 1.084 * D_out^2 + pi * D_out * SF
// D_out uses OD directly, or ID + 2*thk when diameterBasis is ID.
// D / L / thk / SF are in mm in the form and converted to m here.
export function computePaintingAreaSqM(
  { shellRows = [], diameterBasis = "OD", isEnglish = false } = {},
) {
  const isID = String(diameterBasis ?? "").toUpperCase() === "ID";
  // Form stores mm in SI mode, inches in English mode.
  const toM = (val) => (Number(val) || 0) * (isEnglish ? 0.0254 : 0.001);
  let total = 0;
  (Array.isArray(shellRows) ? shellRows : []).forEach((row) => {
    if (!row) return;
    const comp = String(row.Component ?? "").toLowerCase();
    const diaRaw = Number(row.Diameter) || 0;
    if (!(diaRaw > 0)) return;
    const thkRaw = Number(row["Provided Thk"]) || 0;
    const lenRaw = Number(row["Can Length"]) || 0;
    const dOutRaw = isID ? diaRaw + 2 * thkRaw : diaRaw;
    if (!(dOutRaw > 0)) return;
    const dM = toM(dOutRaw);
    if (comp.includes("shell")) {
      if (!(lenRaw > 0)) return;
      total += Math.PI * dM * toM(lenRaw);
    } else if (comp.includes("head")) {
      const sfM = toM(lenRaw || 0);
      total += 1.084 * dM * dM + Math.PI * dM * sfM;
    }
  });
  return roundToTwo(total);
}

// ── Main Estimation WBS summary (DataGridView8) ────────────────────────────────

const SUMMARY_ROW_DEFS = [
  { wbs: "1.1", desc: "Engineering", source: "user", unit: "Manhour" },
  { wbs: "1.2", desc: "QA/QC", source: "user", unit: "Manhour" },
  { wbs: "1.3", desc: "Project Management", source: "user", unit: "Manhour" },
  { wbs: "1.4", desc: "Operations", source: "user", unit: "Manhour" },
  { wbs: "2.1", desc: "Shell & Head Material", source: "shellWeight", unit: "Kg" },
  { wbs: "2.2", desc: "Nozzles Material", source: "nozzleWeight", unit: "Kg" },
  { wbs: "2.3", desc: "Attachments Material", source: "blank", unit: "Kg" },
  { wbs: "3.1", desc: "Shell & Head Assembly", source: "shellFab", unit: "Manhour" },
  { wbs: "3.2", desc: "Nozzle Assembly", source: "nozzleFab", unit: "Manhour" },
  { wbs: "3.3", desc: "Attachments Assembly", source: "blank", unit: "Manhour" },
  { wbs: "3.4", desc: "Hydrotest", source: "user", unit: "Manhour" },
  { wbs: "4.1", desc: "NDT", source: "user", unit: "$" },
  { wbs: "4.2", desc: "Painting", source: "paintingArea", unit: "m²" },
];

/**
 * Builds the fixed 13-row WBS summary that backs the Main Estimation table
 * (DataGridView8). Auto rows (2.1/2.2/3.1/3.2) pull their Est Weight from the
 * Shell/Nozzle BOM items; the rest are filled by the user. Any values already
 * stored for the same WBS code (from a previous estimate or manual edit) are
 * preserved.
 */
export function buildEstimationSummaryRows({
  weights = {},
  fabHours = {},
  paintingArea = null,
  previous = [],
} = {}) {
  const prevByWbs = new Map();
  (Array.isArray(previous) ? previous : []).forEach((row) => {
    if (row && row["WBS Code"]) prevByWbs.set(row["WBS Code"], row);
  });

  const deriveCost = (weight, rate, prev) => {
    const w = String(weight ?? "").trim();
    const r = String(rate ?? "").trim();
    const wn = Number(w);
    const rn = Number(r);
    if (
      w !== "" &&
      r !== "" &&
      Number.isFinite(wn) &&
      Number.isFinite(rn)
    ) {
      return roundToTwo(wn * rn);
    }
    return prev?.["Est Cost"] ?? 0;
  };

  return SUMMARY_ROW_DEFS.map((def, index) => {
    const prev = prevByWbs.get(def.wbs) || {};
    const preserved = {
      Unit: prev.Unit ?? "",
      "Est Weight": prev["Est Weight"] ?? "",
      "Est Unit Rate": prev["Est Unit Rate"] ?? "",
    };

    let row;
    if (def.source === "blank") {
      row = {
        ...preserved,
        Unit: prev.Unit ?? def.unit,
        "Est Weight": prev["Est Weight"] ?? 0,
        "Est Unit Rate": prev["Est Unit Rate"] ?? 0,
      };
    } else if (def.source === "shellWeight") {
      const estWeight = safeNumber(weights.shellWeight);
      row = {
        ...preserved,
        Unit: prev.Unit ?? def.unit,
        "Est Weight": estWeight,
        "Est Unit Rate": prev["Est Unit Rate"] ?? 0,
      };
    } else if (def.source === "nozzleWeight") {
      const estWeight = safeNumber(weights.nozzleWeight);
      row = {
        ...preserved,
        Unit: prev.Unit ?? def.unit,
        "Est Weight": estWeight,
        "Est Unit Rate": prev["Est Unit Rate"] ?? 0,
      };
    } else if (def.source === "shellFab") {
      const estWeight = safeNumber(fabHours.shellFab);
      row = {
        ...preserved,
        Unit: prev.Unit ?? def.unit,
        "Est Weight": estWeight,
        "Est Unit Rate": prev["Est Unit Rate"] ?? 0,
      };
    } else if (def.source === "nozzleFab") {
      const estWeight = safeNumber(fabHours.nozzleFab);
      row = {
        ...preserved,
        Unit: prev.Unit ?? def.unit,
        "Est Weight": estWeight,
        "Est Unit Rate": prev["Est Unit Rate"] ?? 0,
      };
    } else if (def.source === "paintingArea") {
      // Always auto-overwrite from the Shell tab (confirmed behaviour).
      // Quantity unit is m²; keep the user's $/sq.m rate, recompute
      // Est Cost from area x rate.
      const estWeight =
        paintingArea === null || paintingArea === undefined
          ? (prev["Est Weight"] ?? 0)
          : safeNumber(paintingArea);
      row = {
        ...preserved,
        Unit: def.unit,
        "Est Weight": estWeight,
        "Est Unit Rate": prev["Est Unit Rate"] ?? 0,
      };
    } else {
      row = { ...preserved, Unit: prev.Unit ?? def.unit };
    }

    return {
      "WBS Code": def.wbs,
      Description: def.desc,
      ...row,
      "Est Cost": deriveCost(row["Est Weight"], row["Est Unit Rate"], prev),
    };
  }).map((row, index) => ({ ...row, __rowId: `summary-${index}` }));
}
